/**
 * Client Dashboard Component
 *
 * Dashboard view for client users (read-only access to their projects)
 *
 * @module pages/dashboard/ClientDashboard
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, Badge, Alert, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { listProjects } from '../../services/projectService';
import { listQuotations } from '../../services/quotationService';
import { getProgressPhotos } from '../../services/progressService';
import { listInvoices } from '../../services/invoiceService';
import { getProjectMilestones } from '../../services/milestoneService';
import { listTasks } from '../../services/taskService';
import StatCard from '../../components/common/StatCard';
import ImageLightbox from '../../components/common/ImageLightbox';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { formatCurrency, formatDate, formatProjectStatus } from '../../utils/formatters';
import { PROJECT_STATUS, QUOTATION_STATUS } from '../../utils/constants';
import './ClientDashboard.css';

// ---------------------------------------------------------------------------
// #505 (same leak, other door) — this dashboard used to read the `expenses`
// table directly and render three widgets derived from it: "Gastado" per
// project, "Ejecutado" as a total, and "Disponible" = cotizado − gastado.
// That last one IS ProMedic's margin, shown to the counterparty. The identical
// figure is gated two files away in ProjectDetail.js
// (`canViewInternalCosts = isAdmin || isSupervisor`, see the comment block at
// :81) — this page simply had no gate at all.
//
// The fix is not a role check: a client dashboard has NO role for which
// internal cost is appropriate. The expenses read is gone entirely, and the
// money on this page is now money that is genuinely the client's — what they
// were quoted, what they have been invoiced, and what they have paid.
//
// Progress used to be `gastado / cotizado`, i.e. "how much of your money have
// we burned". That is not what a client asks. Progress now comes from
// MILESTONES (falling back to TASKS), which is what "how is my project going"
// actually means. Both tables have a client SELECT policy in prod
// (`Clients can view their project milestones`, `client_view_project_tasks`),
// while `expenses` deliberately has none.
// ---------------------------------------------------------------------------

/**
 * Invoices a client has no business seeing on their own dashboard: a draft has
 * not been issued to them yet, and a cancelled one is not owed.
 */
const HIDDEN_INVOICE_STATUSES = ['draft', 'cancelled'];

/**
 * How much of an invoice is actually paid.
 *
 * `listInvoices` derives `paid_amount` from the `invoice_payments` rows, but an
 * invoice can be marked `paid` without any payment row ever being recorded
 * (that is how the single paid invoice in prod looks today). Trusting only
 * `paid_amount` would tell a client they still owe an invoice they settled.
 */
export const invoicePaidAmount = (invoice) => {
  const amount = invoice?.amount || 0;
  if (invoice?.status === 'paid') return amount;
  return Math.min(invoice?.paid_amount || 0, amount);
};

/**
 * Client-facing money summary for a set of invoices.
 * @returns {{invoiced: number, paid: number, pending: number, overdueCount: number, count: number}}
 */
export const summarizeInvoices = (invoices = []) => {
  const visible = (invoices || []).filter(
    (inv) => inv && !HIDDEN_INVOICE_STATUSES.includes(inv.status)
  );

  const invoiced = visible.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const paid = visible.reduce((sum, inv) => sum + invoicePaidAmount(inv), 0);

  return {
    invoiced,
    paid,
    pending: Math.max(invoiced - paid, 0),
    overdueCount: visible.filter((inv) => inv.is_overdue).length,
    count: visible.length,
  };
};

/**
 * Delivery progress for one project, from what was DELIVERED — never from what
 * was spent.
 *
 * Milestones win when they exist: they are the units ProMedic and the client
 * agreed on, and `weight_percentage` says how much each one is worth. Weights
 * are normalised against their own sum rather than assumed to total 100 (in
 * prod they do not: 29 milestones across several projects sum to 300).
 *
 * Tasks are the fallback so a project without milestones still shows something
 * true instead of a 0% bar that reads like "nothing has happened".
 *
 * @returns {{percent: number, completed: number, total: number, source: 'milestones'|'tasks'|'none'}}
 */
export const computeProjectProgress = ({ milestones, tasks } = {}) => {
  const ms = milestones || [];
  if (ms.length > 0) {
    const isDone = (m) => m.is_completed === true || m.status === 'completed';
    const totalWeight = ms.reduce((sum, m) => sum + (Number(m.weight_percentage) || 0), 0);
    const completed = ms.filter(isDone).length;

    const percent = totalWeight > 0
      ? (ms.filter(isDone).reduce((sum, m) => sum + (Number(m.weight_percentage) || 0), 0) / totalWeight) * 100
      : (completed / ms.length) * 100;

    return {
      percent: Math.min(Math.max(percent, 0), 100),
      completed,
      total: ms.length,
      source: 'milestones',
    };
  }

  const ts = tasks || [];
  if (ts.length > 0) {
    const completed = ts.filter((t) => t.status === 'completed').length;
    return {
      percent: Math.min((completed / ts.length) * 100, 100),
      completed,
      total: ts.length,
      source: 'tasks',
    };
  }

  return { percent: 0, completed: 0, total: 0, source: 'none' };
};

const EMPTY_PROGRESS = { percent: 0, completed: 0, total: 0, source: 'none' };

// ---------------------------------------------------------------------------
// HONESTIDAD DE ERRORES — por qué todas las queries de abajo van envueltas en
// `unwrapServiceResult` y destructuran `isError`.
//
// Los servicios de `src/services/**` RESUELVEN con `{ data: null, error }` en
// vez de lanzar, así que react-query nunca entraba en estado de error y este
// panel hacía `data || []` en los cinco puntos de carga. Resultado: un 401 por
// sesión vencida, la red caída o un 5xx se veían exactamente igual que la
// verdad de negocio — "$0.00 facturado" y "Todavía no hay facturas emitidas",
// que es una AFIRMACIÓN, no un "no sé".
//
// Es el mismo defecto que AdminDashboard ya arregló (ver el comentario de
// `getInventorySummary` ahí: "₡0 facturado es un dato de negocio, no un error
// de red") y que SupervisorDashboard resolvió con Alert + Reintentar. Este era
// el único dashboard que quedaba en cero.
//
// Dos límites explícitos, para no prometer lo que esto no cubre:
//   - El rechazo por RLS NO produce error: PostgREST responde 200 con menos
//     filas y `error: null`. Ningún `isError` atrapa eso. Lo que estas ramas
//     cubren es 401 por JWT vencido, red caída y 5xx.
//   - El fallo es PARCIAL a propósito (referencia: GlobalSearch): si fallan
//     las facturas pero los proyectos cargaron, el monto cotizado se conserva
//     y solo se tapa lo que de verdad no se pudo leer.
// ---------------------------------------------------------------------------

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [allPhotos, setAllPhotos] = useState([]);

  // Una cuenta `role='client'` sin `client_id` no es una empresa sin
  // proyectos: es una cuenta que todavía no está vinculada a ninguna empresa.
  // Hasta ahora los dos casos (y el fallo de carga) caían en la misma frase,
  // "No tienes proyectos asignados", que para el primero es directamente
  // falsa y no dice qué hacer.
  const hasLinkedCompany = !!profile?.client_id;

  // Fetch client's projects. `projects.client_id` is a FK to `clients.id`
  // (the client's company record), NOT to the authenticated user's own
  // profiles/auth id -- use profile.client_id (#308).
  const {
    data: clientProjectsData,
    isLoading: isProjectsLoading,
    isError: isProjectsError,
    error: projectsError,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ['client-projects', profile?.client_id],
    queryFn: () => unwrapServiceResult(listProjects({ clientId: profile?.client_id, pageSize: 20 })),
    enabled: hasLinkedCompany,
  });

  const clientProjects = clientProjectsData?.data || [];
  const totalProjects = clientProjects.length;
  // "Proyecto activo" = trabajo ya comprometido por el cliente. Los tres
  // estados existen en `projects_status_check`; misma definicion que
  // `clientService.getClientStats` (ACTIVE_PROJECT_STATUSES), que la tool MCP
  // `list_clients` y que la funcion `get_client_summary` — si no dicen lo
  // mismo, el mismo cliente ve numeros distintos segun por donde entre.
  // `quotation_accepted` faltaba aca.
  const activeProjects = clientProjects.filter(
    (p) =>
      p.status === PROJECT_STATUS.QUOTATION_ACCEPTED ||
      p.status === PROJECT_STATUS.PO_RECEIVED ||
      p.status === PROJECT_STATUS.IN_PROGRESS
  ).length;

  // Calculate total quoted value
  const totalQuotedValue = clientProjects.reduce(
    (sum, project) => sum + (project.quoted_amount || 0),
    0
  );

  const projectIds = clientProjects.map((p) => p.id);
  const projectIdsKey = [...projectIds].sort().join(',');

  // Invoices for the client's projects. This is the money that IS theirs:
  // what they have been billed and what they have paid. Nothing here is
  // derived from ProMedic's cost.
  const {
    data: invoicesByProjectData,
    isError: isInvoicesError,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['client-project-invoices', projectIdsKey],
    queryFn: async () => {
      const entries = await Promise.all(
        clientProjects.map(async (project) => {
          const { data } = await unwrapServiceResult(listInvoices({ projectId: project.id }));
          return [project.id, data || []];
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: projectIds.length > 0,
  });

  const invoicesByProject = invoicesByProjectData || {};
  const allInvoices = Object.values(invoicesByProject).flat();
  const financials = summarizeInvoices(allInvoices);

  // Delivery progress per project, from milestones (fallback: tasks).
  const {
    data: progressByProjectData,
    isError: isProgressError,
    error: progressError,
    refetch: refetchProgress,
  } = useQuery({
    queryKey: ['client-project-progress', projectIdsKey],
    queryFn: async () => {
      const entries = await Promise.all(
        clientProjects.map(async (project) => {
          const { data: milestones } = await unwrapServiceResult(getProjectMilestones(project.id));
          if (milestones && milestones.length > 0) {
            return [project.id, computeProjectProgress({ milestones })];
          }
          // pageSize well above any real project so the denominator is the
          // whole task list and not the first page of it.
          const { data: tasks } = await unwrapServiceResult(
            listTasks({ projectId: project.id, pageSize: 200 })
          );
          return [project.id, computeProjectProgress({ tasks: tasks || [] })];
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: projectIds.length > 0,
  });

  const progressByProject = progressByProjectData || {};

  // Fetch pending quotations for client
  const {
    data: quotationsData,
    isError: isQuotationsError,
    error: quotationsError,
    refetch: refetchQuotations,
  } = useQuery({
    queryKey: ['client-quotations', user?.id],
    queryFn: async () => {
      const allQuotations = [];
      for (const project of clientProjects) {
        const result = await unwrapServiceResult(listQuotations({
          projectId: project.id,
          status: QUOTATION_STATUS.SENT
        }));
        if (result.data) {
          allQuotations.push(...result.data);
        }
      }
      return allQuotations;
    },
    enabled: clientProjects.length > 0,
  });

  const pendingQuotations = quotationsData || [];

  // Fetch recent progress photos
  const {
    data: recentPhotosData,
    isError: isPhotosError,
    error: photosError,
    refetch: refetchPhotos,
  } = useQuery({
    queryKey: ['client-recent-photos', user?.id],
    queryFn: async () => {
      const photos = [];
      for (const project of clientProjects.slice(0, 5)) {
        const result = await unwrapServiceResult(
          getProgressPhotos(project.id, { limit: 6, ascending: false })
        );
        if (result.data) {
          photos.push(...result.data.map(photo => ({ ...photo, project })));
        }
      }
      return photos.slice(0, 12);
    },
    enabled: clientProjects.length > 0,
  });

  const recentPhotos = recentPhotosData || [];

  // El detalle técnico va a la consola, nunca a la pantalla del cliente: son
  // strings de PostgREST que nombran tablas y columnas internas. Mismo criterio
  // que AdminDashboard:110-121 y SupervisorDashboard:116-127.
  if (isProjectsError) {
    // eslint-disable-next-line no-console
    console.error('[ClientDashboard] listProjects falló', projectsError);
  }
  if (isInvoicesError) {
    // eslint-disable-next-line no-console
    console.error('[ClientDashboard] listInvoices falló', invoicesError);
  }
  if (isProgressError) {
    // eslint-disable-next-line no-console
    console.error('[ClientDashboard] avance por proyecto falló', progressError);
  }
  if (isQuotationsError) {
    // eslint-disable-next-line no-console
    console.error('[ClientDashboard] listQuotations falló', quotationsError);
  }
  if (isPhotosError) {
    // eslint-disable-next-line no-console
    console.error('[ClientDashboard] getProgressPhotos falló', photosError);
  }

  // Si los proyectos no cargaron, las facturas ni siquiera se pidieron (la
  // query está `enabled` sobre projectIds): en los dos casos la verdad es la
  // misma — no sabemos cuánto se facturó — y decir "$0.00" sería inventar.
  const invoicesUnavailable = isProjectsError || isInvoicesError;

  const handlePhotoClick = (index) => {
    setAllPhotos(recentPhotos);
    setSelectedPhotoIndex(index);
  };

  // How much of what the client has been invoiced is already settled.
  // Replaces the old "Utilización del Presupuesto" (= gastado / cotizado),
  // which measured ProMedic's spend, not anything the client owns.
  const paymentProgress = financials.invoiced > 0
    ? Math.min((financials.paid / financials.invoiced) * 100, 100)
    : 0;

  return (
    <DashboardLayout>
      <Container fluid className="client-dashboard">
        {/* Header */}
        <Row className="mb-4">
        <Col>
          <div className="dashboard-header">
            <div>
              <h1 className="h3 mb-1">Portal del Cliente</h1>
              <p className="text-muted mb-0">Bienvenido, {profile?.full_name || 'Cliente'}</p>
            </div>
            <Button
              variant="outline-primary"
              onClick={() => navigate('/contacto')}
              className="contact-btn"
            >
              <i className="bi bi-headset me-2"></i>
              Contactar a ProMedic
            </Button>
          </div>
        </Col>
      </Row>

      {/* Stats Cards
          Con la carga caída estas tarjetas pintaban 0 y "$0.00" con la misma
          confianza que un dato real. El guion no se explica solo: lo explica
          el bloque de error de "Mis Proyectos", que es donde está el botón
          para reintentar. */}
      <Row className="g-3 mb-4">
        <Col md={6} lg={3}>
          <StatCard
            title="Mis Proyectos"
            value={isProjectsError ? '—' : totalProjects}
            loading={isProjectsLoading}
            icon="bi-folder"
            color="primary"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            title="Proyectos Activos"
            value={isProjectsError ? '—' : activeProjects}
            loading={isProjectsLoading}
            icon="bi-folder-check"
            color="success"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            title="Inversión Total"
            value={isProjectsError ? '—' : formatCurrency(totalQuotedValue)}
            loading={isProjectsLoading}
            icon="bi-cash-stack"
            color="info"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            title="Cotizaciones Pendientes"
            value={isProjectsError || isQuotationsError ? '—' : pendingQuotations.length}
            icon="bi-file-text"
            color="warning"
          />
        </Col>
      </Row>

      {/* Una cotización pendiente que no se pudo leer es plata parada: el
          cliente no puede aprobar lo que no ve, y un "0" ahí arriba le dice
          justo lo contrario. */}
      {isQuotationsError && (
        <Row className="mb-4">
          <Col>
            <Alert variant="warning" data-testid="client-quotations-error">
              <Alert.Heading className="h6 mb-1">
                <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                No pudimos revisar si tenés cotizaciones pendientes
              </Alert.Heading>
              <p className="small mb-2">
                Es un problema de conexión con el servidor, no una bandeja vacía.
              </p>
              <Button
                variant="outline-warning"
                size="sm"
                data-testid="client-quotations-retry"
                onClick={() => refetchQuotations()}
              >
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                Reintentar
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Pending Quotations Alert */}
      {pendingQuotations.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Alert variant="warning" className="quotation-alert">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>Tienes {pendingQuotations.length} cotización(es) pendiente(s) de revisión</strong>
                </div>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => {
                    const firstQuotation = pendingQuotations[0];
                    if (firstQuotation) {
                      navigate(`/cotizaciones/${firstQuotation.id}`);
                    }
                  }}
                >
                  Ver Cotizaciones
                </Button>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Active Projects */}
      <Row className="mb-4">
        <Col>
          <Card className="project-cards-container">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-folder-open me-2"></i>
                Mis Proyectos
              </h5>
              {clientProjects.length > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate('/proyectos')}
                >
                  Ver Todos
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {/* Tres estados que hasta acá se veían idénticos:
                    1. la cuenta no está vinculada a ninguna empresa,
                    2. la empresa no tiene proyectos,
                    3. la consulta falló.
                  Los tres decían "No tienes proyectos asignados". El primero
                  es falso (no es un problema de proyectos, es de
                  aprovisionamiento de la cuenta) y el tercero es la mentira
                  cara: afirma que no hay nada cuando lo que pasó es que no se
                  pudo leer. */}
              {!hasLinkedCompany ? (
                <div className="text-center py-5" data-testid="client-no-company">
                  <i className="bi bi-building-exclamation" style={{ fontSize: '3rem' }}></i>
                  <h6 className="mt-3">Tu cuenta todavía no está vinculada a ninguna empresa</h6>
                  <p className="text-muted small mb-3">
                    Sin ese vínculo no podemos mostrarte proyectos, cotizaciones ni
                    facturas. No es que no tengas proyectos: es que todavía no
                    sabemos de qué empresa sos. Escribinos y lo dejamos listo.
                  </p>
                  <Button variant="primary" size="sm" onClick={() => navigate('/contacto')}>
                    <i className="bi bi-headset me-1" aria-hidden="true"></i>
                    Contactar a ProMedic
                  </Button>
                </div>
              ) : isProjectsError ? (
                <Alert variant="danger" data-testid="client-projects-error">
                  <Alert.Heading className="h6 mb-1">
                    <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                    No pudimos cargar tus proyectos
                  </Alert.Heading>
                  <p className="small mb-2">
                    Es un problema de conexión con el servidor, no una lista vacía.
                    Volvé a intentarlo; si sigue fallando, avisanos.
                  </p>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    data-testid="client-projects-retry"
                    onClick={() => refetchProjects()}
                  >
                    <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                    Reintentar
                  </Button>
                </Alert>
              ) : isProjectsLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="text-muted mb-0">Cargando tus proyectos...</p>
                </div>
              ) : clientProjects.length > 0 ? (
                <Row className="g-3">
                  {clientProjects.slice(0, 6).map((project) => {
                    const statusInfo = formatProjectStatus(project.status);
                    const projectMoney = summarizeInvoices(invoicesByProject[project.id]);
                    const progress = progressByProject[project.id] || EMPTY_PROGRESS;
                    const progressLabel = progress.source === 'milestones'
                      ? `Avance (${progress.completed}/${progress.total} hitos)`
                      : progress.source === 'tasks'
                        ? `Avance (${progress.completed}/${progress.total} tareas)`
                        : 'Avance';

                    return (
                      <Col md={6} lg={4} key={project.id}>
                        <Card
                          className="project-card h-100 cursor-pointer"
                          onClick={() => navigate(`/proyectos/${project.id}`)}
                        >
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <code className="project-number">{project.project_number}</code>
                                <h6 className="mt-2 mb-1">{project.title}</h6>
                              </div>
                              <Badge bg={statusInfo.variant} className="status-badge">
                                {statusInfo.label}
                              </Badge>
                            </div>

                            <div className="project-stats mb-3">
                              <div className="stat-item">
                                <small className="text-muted">Monto Cotizado</small>
                                <div className="fw-bold text-primary">
                                  {formatCurrency(project.quoted_amount || 0)}
                                </div>
                              </div>
                              <div className="stat-item">
                                <small className="text-muted">Pagado</small>
                                <div className="fw-bold" data-testid="project-card-paid">
                                  {invoicesUnavailable
                                    ? <span className="text-muted fw-normal small">No disponible</span>
                                    : formatCurrency(projectMoney.paid)}
                                </div>
                              </div>
                            </div>

                            <div className="mb-2">
                              {/* Una barra en 0% dice "no avanzó nada". Si el
                                  avance no se pudo leer, eso es otra cosa. */}
                              {isProgressError ? (
                                <div data-testid="project-card-progress-error">
                                  <small className="text-muted d-block">
                                    <i className="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>
                                    No pudimos cargar el avance de este proyecto.
                                  </small>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    data-testid="project-card-progress-retry"
                                    onClick={(e) => { e.stopPropagation(); refetchProgress(); }}
                                  >
                                    Reintentar
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <div className="d-flex justify-content-between mb-1">
                                    <small className="text-muted">{progressLabel}</small>
                                    <small className="fw-bold">{progress.percent.toFixed(0)}%</small>
                                  </div>
                                  <ProgressBar
                                    now={progress.percent}
                                    variant={progress.percent >= 100 ? 'success' : 'info'}
                                    style={{ height: '8px' }}
                                  />
                                  {progress.source === 'none' && (
                                    <small className="text-muted d-block mt-1">
                                      Aún no hay hitos ni tareas registradas
                                    </small>
                                  )}
                                </>
                              )}
                            </div>

                            <div className="project-meta">
                              <small className="text-muted">
                                <i className="bi bi-calendar me-1"></i>
                                {formatDate(project.created_at)}
                              </small>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-folder-x" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3 mb-0">No tienes proyectos asignados</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Pending Quotations Section */}
      {pendingQuotations.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card className="quotations-container">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Cotizaciones Pendientes de Aprobación
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {pendingQuotations.slice(0, 3).map((quotation) => (
                    <Col md={6} lg={4} key={quotation.id}>
                      <Card className="quotation-card h-100">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <code className="quotation-number">
                                {quotation.quotation_number || 'N/A'}
                              </code>
                              <h6 className="mt-2 mb-1">
                                {quotation.project?.title || 'Proyecto'}
                              </h6>
                            </div>
                            <Badge bg="warning" text="dark">
                              Pendiente
                            </Badge>
                          </div>

                          <div className="quotation-total mb-3">
                            <small className="text-muted d-block">Monto Total</small>
                            <h4 className="mb-0 text-primary">
                              {formatCurrency(quotation.total || 0)}
                            </h4>
                          </div>

                          <div className="quotation-breakdown mb-3">
                            <div className="d-flex justify-content-between">
                              <small className="text-muted">Subtotal:</small>
                              <small>{formatCurrency(quotation.subtotal || 0)}</small>
                            </div>
                            <div className="d-flex justify-content-between">
                              <small className="text-muted">IVA ({quotation.tax_rate}%):</small>
                              <small>{formatCurrency(quotation.tax_amount || 0)}</small>
                            </div>
                          </div>

                          <div className="d-grid gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigate(`/cotizaciones/${quotation.id}`)}
                            >
                              <i className="bi bi-eye me-1"></i>
                              Ver Detalles
                            </Button>
                          </div>

                          <div className="quotation-meta mt-2">
                            <small className="text-muted">
                              <i className="bi bi-calendar me-1"></i>
                              Enviada: {formatDate(quotation.created_at)}
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="mb-4">
        {/* Financial Summary — the client's own money only */}
        <Col lg={6}>
          <Card className="financial-overview h-100" data-testid="client-financial-summary">
            <Card.Header>
              <h5 className="mb-0">
                <i className="bi bi-receipt me-2"></i>
                Resumen Financiero
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="financial-chart mb-4">
                <div className="chart-header mb-3">
                  <div className="chart-value">
                    <small className="text-muted d-block">Monto Cotizado</small>
                    <h3 className="mb-0 text-primary" data-testid="client-total-quoted">
                      {isProjectsError ? '—' : formatCurrency(totalQuotedValue)}
                    </h3>
                  </div>
                  {/* Fallo parcial: si los proyectos cargaron y las facturas no,
                      el monto cotizado de arriba SIGUE siendo verdad y se
                      conserva. Lo único que se tapa es lo que no se pudo leer. */}
                  {!invoicesUnavailable && (
                    <div className="chart-value">
                      <small className="text-muted d-block">Facturado</small>
                      <h3 className="mb-0" data-testid="client-total-invoiced">
                        {formatCurrency(financials.invoiced)}
                      </h3>
                    </div>
                  )}
                </div>

                {!invoicesUnavailable && (
                  <>
                    <div className="payment-bar mb-2">
                      <div
                        className="payment-fill"
                        style={{ width: `${paymentProgress}%` }}
                      ></div>
                    </div>

                    <div className="d-flex justify-content-between">
                      <small className="text-muted">Facturas Pagadas</small>
                      <small className="fw-bold">{paymentProgress.toFixed(1)}%</small>
                    </div>
                  </>
                )}
              </div>

              {invoicesUnavailable && (
                <Alert variant="warning" className="mb-0" data-testid="client-invoices-error">
                  <Alert.Heading className="h6 mb-1">
                    <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                    No pudimos cargar tu facturación
                  </Alert.Heading>
                  <p className="small mb-2">
                    Los montos quedarían en cero sin que eso sea real. No quiere decir
                    que no tengas facturas: quiere decir que ahora no las podemos leer.
                  </p>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    data-testid="client-invoices-retry"
                    onClick={() => (isProjectsError ? refetchProjects() : refetchInvoices())}
                  >
                    <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                    Reintentar
                  </Button>
                </Alert>
              )}

              {!invoicesUnavailable && (
              <div className="value-indicators">
                <div className="indicator-item">
                  <div className="indicator-icon bg-success-subtle">
                    <i className="bi bi-check-circle text-success"></i>
                  </div>
                  <div>
                    <small className="text-muted d-block">Pagado</small>
                    <strong className="text-success" data-testid="client-total-paid">
                      {formatCurrency(financials.paid)}
                    </strong>
                  </div>
                </div>
                <div className="indicator-item">
                  <div className="indicator-icon bg-warning-subtle">
                    <i className="bi bi-hourglass-split text-warning"></i>
                  </div>
                  <div>
                    <small className="text-muted d-block">Pendiente de Pago</small>
                    <strong className="text-warning" data-testid="client-total-pending">
                      {formatCurrency(financials.pending)}
                    </strong>
                  </div>
                </div>
                {financials.overdueCount > 0 && (
                  <div className="indicator-item">
                    <div className="indicator-icon bg-danger-subtle">
                      <i className="bi bi-exclamation-octagon text-danger"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">Facturas Vencidas</small>
                      <strong className="text-danger" data-testid="client-overdue-count">
                        {financials.overdueCount}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Esta frase es una AFIRMACIÓN sobre el negocio del cliente, y
                  por eso solo puede decirse cuando de verdad se leyeron las
                  facturas. Antes también se imprimía cuando la lectura había
                  fallado, que es la peor versión del defecto. */}
              {!invoicesUnavailable && financials.count === 0 && (
                <p className="text-muted small mb-0 mt-3">
                  Todavía no hay facturas emitidas para tus proyectos.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Contact Section */}
        <Col lg={6}>
          <Card className="contact-card h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="bi bi-headset me-2"></i>
                ¿Necesitas Ayuda?
              </h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <p className="text-muted mb-4">
                Nuestro equipo está disponible para atender tus consultas y brindarte el mejor servicio.
              </p>

              <div className="contact-info mb-4">
                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="bi bi-building"></i>
                  </div>
                  <div>
                    <strong>ProMedic Group</strong>
                    <small className="text-muted d-block">Electromecánica Especializada</small>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="bi bi-envelope"></i>
                  </div>
                  <div>
                    <a href="mailto:jorge.murillo@promedicgroup.com">
                      jorge.murillo@promedicgroup.com
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="bi bi-telephone"></i>
                  </div>
                  <div>
                    <a href="tel:+50688377017">+506 8837-7017</a>
                  </div>
                </div>
              </div>

              <div className="mt-auto d-grid gap-2">
                <Button
                  variant="primary"
                  href="mailto:jorge.murillo@promedicgroup.com"
                >
                  <i className="bi bi-envelope-fill me-2"></i>
                  Enviar Correo
                </Button>
                <Button
                  variant="outline-primary"
                  href="tel:+50688377017"
                >
                  <i className="bi bi-telephone-fill me-2"></i>
                  Llamar Ahora
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Galería de progreso — un fallo acá hacía desaparecer la sección
          entera sin decir nada, que se lee como "no hay fotos". */}
      {isPhotosError && (
        <Row className="mb-4">
          <Col>
            <Alert variant="warning" data-testid="client-photos-error">
              <Alert.Heading className="h6 mb-1">
                <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                No pudimos cargar la galería de progreso
              </Alert.Heading>
              <p className="small mb-2">
                Puede que sí haya fotos nuevas de tus proyectos; ahora no las podemos leer.
              </p>
              <Button
                variant="outline-warning"
                size="sm"
                data-testid="client-photos-retry"
                onClick={() => refetchPhotos()}
              >
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                Reintentar
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Recent Progress Photos Gallery */}
      {recentPhotos.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card className="photos-gallery">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-images me-2"></i>
                  Galería de Progreso
                </h5>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate('/progreso/galeria')}
                >
                  Ver Todas
                </Button>
              </Card.Header>
              <Card.Body>
                <div className="photo-grid">
                  {recentPhotos.map((photo, index) => (
                    <button
                      key={photo.id}
                      type="button"
                      className="photo-item"
                      onClick={() => handlePhotoClick(index)}
                      aria-label={`Ampliar foto: ${photo.caption || 'foto de progreso'}${
                        photo.project?.project_number ? ` — ${photo.project.project_number}` : ''
                      }`}
                    >
                      <img
                        src={photo.photo_url}
                        alt={photo.caption || 'Foto de progreso'}
                        className="photo-thumbnail"
                      />
                      <div className="photo-overlay">
                        <div className="photo-info">
                          <small className="photo-project">
                            {photo.project?.project_number}
                          </small>
                          {photo.caption && (
                            <small className="photo-caption">{photo.caption}</small>
                          )}
                          <small className="photo-date">
                            {formatDate(photo.uploaded_at)}
                          </small>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Image Lightbox */}
      {selectedPhotoIndex !== null && (
        <ImageLightbox
          images={allPhotos}
          currentIndex={selectedPhotoIndex}
          onClose={() => setSelectedPhotoIndex(null)}
          onNext={() => setSelectedPhotoIndex((prev) => Math.min(prev + 1, allPhotos.length - 1))}
          onPrev={() => setSelectedPhotoIndex((prev) => Math.max(prev - 1, 0))}
        />
      )}
      </Container>
    </DashboardLayout>
  );
};

export default ClientDashboard;
